import React from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { Text } from '../text'

export type UnitTaskProgressBarProps = {
    /** Completed count (clamped to `[0, max]`). */
    value: number
    /** Total count. When `0` the bar renders empty. */
    max: number
    className?: string
    testID?: string
}

const clampRatio = (value: number, max: number): number => {
    if (max <= 0) {
        return 0
    }
    return Math.min(Math.max(value / max, 0), 1)
}

/**
 * Unit Task List progress bar (Figma `ProgressBar`, node 10779:89695): an 8px
 * rounded track in `bg/elevation` with an `action`-blue filled portion. The
 * fill width is `value / max`; the fill keeps a fully rounded cap so a partial
 * bar reads correctly. Presentational — the done/total counting lives in the
 * caller (see `PhaseProgressBar` for the labelled variant).
 */
export const UnitTaskProgressBar: React.FC<UnitTaskProgressBarProps> = ({
    value,
    max,
    className,
    testID,
}) => {
    const ratio = clampRatio(value, max)
    return (
        <Box
            className={twMerge(
                'h-2 w-full overflow-hidden rounded-full bg-aos-bg-elevation',
                className,
            )}
            testID={testID}
        >
            {ratio > 0 ? (
                <Box
                    className='h-full rounded-full bg-action-default'
                    style={{ width: `${ratio * 100}%` }}
                />
            ) : null}
        </Box>
    )
}

UnitTaskProgressBar.displayName = 'UnitTaskProgressBar'

export type PhaseProgressBarProps = {
    /** Completed count (done + skipped). */
    value: number
    /** Total task count in the phase. */
    max: number
    /**
     * Left label (Figma `FINISHED: n`, success green). When the phase is fully
     * complete the caller typically passes the completed summary here.
     */
    finishedText?: string
    /** Right label (Figma `TO DO: n`, critical red). Hidden when omitted. */
    todoText?: string
    className?: string
    testID?: string
}

/**
 * Unit Task List phase progress bar (Figma `PhaseProgressBar`, node
 * 10779:89643): the {@link UnitTaskProgressBar} with a `FINISHED` (success) /
 * `TO DO` (critical) caption row underneath. Both labels are passed in already
 * translated — the i18n + completed-vs-finished wording stays in the caller.
 */
export const PhaseProgressBar: React.FC<PhaseProgressBarProps> = ({
    value,
    max,
    finishedText,
    todoText,
    className,
    testID,
}) => (
    <Box className={twMerge('w-full gap-2', className)} testID={testID}>
        <UnitTaskProgressBar value={value} max={max} />
        {finishedText || todoText ? (
            <Box className='flex-row items-center justify-between'>
                <Text size='caption' tone='success' className='uppercase tracking-wide'>
                    {finishedText}
                </Text>
                {todoText ? (
                    <Text size='caption' tone='critical' className='uppercase tracking-wide'>
                        {todoText}
                    </Text>
                ) : null}
            </Box>
        ) : null}
    </Box>
)

PhaseProgressBar.displayName = 'PhaseProgressBar'
